import type { FormatterFn, FormatOptions } from './types';

const formatterCache = new Map<string, Intl.NumberFormat>();

function isFormatOptions(options: any): options is FormatOptions {
  return options && (
    'useMillions' in options ||
    'millionsFrom' in options ||
    'decimalPlaces' in options
  );
}

export function createNumberFormatter(
  locale: string = 'ru-RU',
  options: Intl.NumberFormatOptions = {}
): FormatterFn<number | null> {
  const cacheKey = JSON.stringify({ locale, options });

  if (!formatterCache.has(cacheKey)) {
    formatterCache.set(cacheKey, new Intl.NumberFormat(locale, options));
  }

  const formatter = formatterCache.get(cacheKey)!;

  return (value) => {
    if (value === null || value === undefined) {
      return '—';
    }

    return formatter.format(value);
  };
}

export function formatNumber(
  value: number | null | undefined,
  options?: FormatOptions | Intl.NumberFormatOptions
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'number' && Number.isNaN(value)) {
    return '—';
  }

  if (isFormatOptions(options)) {
    let scaledValue = value;

    if (options.useMillions) {
      const threshold = options.millionsFrom ?? 0;
      if (Math.abs(scaledValue) >= threshold) {
        scaledValue = scaledValue / 1_000_000;
      }
    }

    const intlOptions: Intl.NumberFormatOptions = {};

    if (options.decimalPlaces === null) {
      intlOptions.maximumFractionDigits = 0;
      intlOptions.minimumFractionDigits = 0;
    } else if (options.decimalPlaces !== undefined) {
      intlOptions.maximumFractionDigits = options.decimalPlaces;
      intlOptions.minimumFractionDigits = options.decimalPlaces;
    }

    const formatter = createNumberFormatter('ru-RU', intlOptions);
    return formatter(scaledValue);
  } else {
    const formatter = createNumberFormatter('ru-RU', options || {});
    return formatter(value);
  }
}

export function formatDelta(
  value: number | null,
  options?: FormatOptions | Intl.NumberFormatOptions
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const sign = value > 0 ? '+' : (value < 0 ? '-' : '');
  return `${sign}${formatNumber(Math.abs(value), options)}`;
}
